/* eslint-disable max-lines, max-lines-per-function */
import { useState, useEffect, useRef } from "react";
import { isMediumDeviceAndUp } from "common/utils/browser";
import { generateUUID } from "common/utils/uuid";
import {
  trackTransfersPinClarificationClicked,
  trackTransfersPinCreated,
  trackTransfersPinDeleted,
  trackTransfersPinDeleteFailed,
  trackTransfersPinError,
  trackTransfersPinMessageSubmitted,
  trackTransfersPinNeedsClarification,
  trackTransfersPinReady,
  trackTransfersPinSelected,
  trackTransfersPinUnsupported
} from "../transfersPinningAnalytics";
import {
  removePin as removePinApi,
  submitPinMessage,
  updatePinInstruction as updatePinInstructionApi
} from "./usePinningSession";

const MIN_MESSAGE_LENGTH = 1;
const MAX_PINS = 20;

export const PIN_COLORS = ["#3B82F6", "#8B5CF6", "#EC4899", "#F59E0B", "#14B8A6"];

/**
 * Gets the effective answer value for a clarification response.
 * @param {object | undefined} response - { selected, custom, isTypingCustom }
 * @returns {string | null}
 */
export function getClarificationAnswer(response) {
  if (!response) return null;
  if (response.isTypingCustom) {
    return response.custom?.trim() || null;
  }
  return response.selected || null;
}

/**
 * Core hook for managing pinning panel state and orchestration.
 * All ephemeral state lives here and is garbage-collected when the component unmounts.
 *
 * @param {string | null} sessionId - The current session ID
 * @param {string | null} artworkId - The current artwork ID
 * @returns {object} Panel state, computed values, and handlers
 */
export function usePinningPanel(sessionId, artworkId, { toast, pendingInitialMessage } = {}) {
  const [isDesktop, setIsDesktop] = useState(!!isMediumDeviceAndUp()); // eslint-disable-line no-unused-vars
  const [pins, setPins] = useState([]);
  const [activePinId, setActivePinId] = useState(null);
  const [hoveredPinId, setHoveredPinId] = useState(null);
  const [hoveredPinSource, setHoveredPinSource] = useState(null);
  const [composerText, setComposerText] = useState("");
  const [removingPinIds, setRemovingPinIds] = useState(new Set());
  const [pendingDeletePinId, setPendingDeletePinId] = useState(null);
  const [clarificationResponses, setClarificationResponses] = useState({});
  const [mobileView, setMobileView] = useState("list");
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [editingPinId, setEditingPinId] = useState(null);
  const [isUpdatingPinInstruction, setIsUpdatingPinInstruction] = useState(false);
  const [lastEditedPinId, setLastEditedPinId] = useState(null);
  const [unansweredAbove, setUnansweredAbove] = useState(0);
  const [unansweredBelow, setUnansweredBelow] = useState(0);
  const [showPins, setShowPins] = useState(true); // eslint-disable-line no-unused-vars

  const textareaRef = useRef(null);
  const [pendingCursorPosition, setPendingCursorPosition] = useState(null);
  const clarificationScrollRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const pinsContainerRef = useRef(null);

  let savedClarificationResponses = null;
  let savedPinState = null;
  let savedMessages = null;
  let savedComposerText = null;
  let abortController = null;

  function activePin() {
    return pins.find(p => p.id === activePinId) ?? null;
  }

  function editingPin() {
    return pins.find(p => p.id === editingPinId) ?? null;
  }

  function hasCompletedPin() {
    return pins.some(pin => pin.state === "ready");
  }

  function readyPinsCount() {
    return pins.filter(pin => pin.state === "ready").length;
  }

  function hasExistingPin() {
    return pins.some(pin => !pin.isNew);
  }

  function hasAiAssistanceOngoing() {
    return pins.some(pin => pin.state === "processing" || pin.state === "needs_clarification");
  }

  function hasUnfinishedPin() {
    return pins.some(
      pin =>
        pin.state === "draft" || pin.state === "processing" || pin.state === "needs_clarification"
    );
  }

  function canAddNewPin() {
    return (
      pins.length < MAX_PINS &&
      (!activePin() || activePin().state === "ready" || activePin().state === "unsupported")
    );
  }

  function canRepositionActivePin() {
    const active = activePin();
    if (!active) return false;
    const { state, messages } = active;
    return state === "draft" && messages.length === 0;
  }

  function lastAiMessageWithClarifications() {
    const active = activePin();
    if (!active) return null;
    const { messages } = active;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const msg = messages[i];
      if (msg?.role === "ai" && msg.clarifications && msg.clarifications.length > 0) return msg;
    }
    return null;
  }

  function pendingPillClarifications() {
    const clarifications = lastAiMessageWithClarifications()?.clarifications ?? [];
    return clarifications.filter(c => c.status === "asking" && c.options.length > 0);
  }

  function fontClarification() {
    return pendingPillClarifications().find(c => c.type === "font_selection");
  }

  function colorClarification() {
    return pendingPillClarifications().find(c => c.type === "font_color");
  }

  function selectedFontValue() {
    const font = fontClarification();
    if (!font) return undefined;
    const response = clarificationResponses[font.question];
    if (response?.isTypingCustom) return response.custom || undefined;
    if (response?.selected) {
      const opt = font.options.find(o => o.label === response.selected);
      return opt?.value || response.selected;
    }
    return undefined;
  }

  function selectedColorValue() {
    const color = colorClarification();
    if (!color) return undefined;
    const response = clarificationResponses[color.question];
    if (response?.isTypingCustom) return response.custom || undefined;
    if (response?.selected) {
      const opt = color.options.find(o => o.label === response.selected);
      return opt?.value || response.selected;
    }
    return undefined;
  }

  function hasAnyClarificationResponse() {
    return pendingPillClarifications().some(c =>
      getClarificationAnswer(clarificationResponses[c.question])
    );
  }

  function updatePin(pinId, updater) {
    setPins(prev => prev.map(pin => (pin.id === pinId ? updater(pin) : pin)));
  }

  function deletePin(pinId) {
    setPins(prev => prev.filter(p => p.id !== pinId));
    if (activePinId === pinId) {
      setActivePinId(null);
    }
  }

  function clearRemovingPin(pinId) {
    setRemovingPinIds(prev => {
      const next = new Set(prev);
      next.delete(pinId);
      return next;
    });
  }

  function isComposerTextValid(text) {
    return text.trim().length >= MIN_MESSAGE_LENGTH;
  }

  function repositionActivePin(coordinates) {
    if (!canRepositionActivePin()) return;
    updatePin(activePinId, pin => ({ ...pin, coordinates }));
  }

  function addPin(coordinates) {
    if (!canAddNewPin()) return null;

    const newPin = {
      id: generateUUID(),
      coordinates,
      state: "draft",
      messages: [],
      instruction: undefined,
      areasOfInterest: undefined,
      segmentationMaskUrl: undefined,
      highlightAreasEnabled: undefined,
      thinkingText: undefined,
      isNew: true
    };

    setPins(prev => [...prev, newPin]);
    setActivePinId(newPin.id);

    if (!isDesktop) {
      setMobileView("detail");
    }

    if (sessionId && artworkId) {
      trackTransfersPinCreated({
        artworkId,
        sessionId,
        pinId: newPin.id
      });
    }

    return newPin;
  }

  function handleDeletePin(pinId) {
    const pin = pins.find(p => p.id === pinId);
    if (pin?.state === "processing") {
      abortController?.abort();
    }

    if (!sessionId) {
      deletePin(pinId);
      return;
    }

    const hasBeenSyncedToBackend = (pin?.messages?.length ?? 0) > 0 || pin?.state !== "draft";

    if (!hasBeenSyncedToBackend) {
      if (artworkId) {
        trackTransfersPinDeleted({
          artworkId,
          sessionId,
          pinId,
          pinState: pin?.state ?? "draft",
          hadMessages: false
        });
      }
      deletePin(pinId);
      if (!isDesktop) {
        setMobileView("list");
      }
      return;
    }

    if (removingPinIds.has(pinId)) return;

    setRemovingPinIds(prev => new Set(prev).add(pinId));

    removePinApi(sessionId, pinId)
      .then(() => {
        if (artworkId) {
          trackTransfersPinDeleted({
            artworkId,
            sessionId,
            pinId,
            pinState: pin?.state ?? "draft",
            hadMessages: (pin?.messages?.length ?? 0) > 0
          });
        }
        deletePin(pinId);
        clearRemovingPin(pinId);
        if (!isDesktop) {
          setMobileView("list");
        }
      })
      .catch(() => {
        if (artworkId) {
          trackTransfersPinDeleteFailed({ artworkId, sessionId, pinId });
        }
        clearRemovingPin(pinId);
        toast?.error({
          title: "Failed to remove pin",
          description: "Something went wrong. Please try again."
        });
      });
  }

  function openDeletePinModal(pinId) {
    setPendingDeletePinId(pinId);
  }

  function closeDeletePinModal() {
    setPendingDeletePinId(null);
  }

  function confirmDeletePin() {
    if (!pendingDeletePinId) return;
    handleDeletePin(pendingDeletePinId);
    setPendingDeletePinId(null);
  }

  function openEditPinModal(pinId) {
    setEditingPinId(pinId);
  }

  function closeEditPinModal() {
    setEditingPinId(null);
  }

  function handleUpdatePinInstruction(instruction) {
    if (!editingPinId || !sessionId) return;

    setIsUpdatingPinInstruction(true);

    updatePinInstructionApi(sessionId, editingPinId, instruction)
      .then(() => {
        updatePin(editingPinId, pin => ({ ...pin, instruction }));
        setEditingPinId(null);
      })
      .catch(() => {
        toast?.error({
          title: "Failed to update pin",
          description: "Something went wrong. Please try again."
        });
      })
      .finally(() => {
        setIsUpdatingPinInstruction(false);
      });
  }

  function handleSelectClarificationOption(question, value) {
    const prev = clarificationResponses;

    const newResponses = {
      ...prev,
      [question]: {
        selected: value,
        custom: prev[question]?.custom ?? "",
        isTypingCustom: false
      }
    };

    setClarificationResponses(newResponses);

    const currentPin = activePin();
    if (sessionId && artworkId && currentPin) {
      const userCount = currentPin.messages.filter(m => m.role === "user").length;
      const aiCount = currentPin.messages.filter(m => m.role === "ai").length;
      trackTransfersPinClarificationClicked({
        artworkId,
        sessionId,
        pinId: currentPin.id,
        optionValue: value || "",
        turnIndex: userCount + 1,
        userMessagesCount: userCount,
        aiMessagesCount: aiCount,
        optionType: "suggested"
      });
    }

    if (value !== null) {
      const clarification = pendingPillClarifications().find(c => c.question === question);
      if (clarification?.type === "finalize_confirmation") {
        handleSubmit();
        return;
      }
    }

    if (value !== null && !getClarificationAnswer(prev[question])) {
      setTimeout(() => {
        const currentIndex = pendingPillClarifications().findIndex(c => c.question === question);
        const reorderedClarifications = [
          ...pendingPillClarifications().slice(currentIndex + 1),
          ...pendingPillClarifications().slice(0, currentIndex)
        ];

        const nextUnanswered = reorderedClarifications.find(
          c => !getClarificationAnswer(newResponses[c.question])
        );

        if (nextUnanswered && clarificationScrollRef.current) {
          const labelValue = nextUnanswered.label || nextUnanswered.question;
          const targetElement = clarificationScrollRef.current.querySelector(
            `[data-clarification-label="${CSS.escape(labelValue)}"]`
          );
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }
      }, 100);
    }
  }

  function handleToggleCustomInput(question) {
    setClarificationResponses(prev => ({
      ...prev,
      [question]: {
        selected: prev[question]?.selected ?? null,
        custom: prev[question]?.custom ?? "",
        isTypingCustom: !prev[question]?.isTypingCustom
      }
    }));

    const currentPin = activePin();
    if (sessionId && artworkId && currentPin) {
      const userCount = currentPin.messages.filter(m => m.role === "user").length;
      const aiCount = currentPin.messages.filter(m => m.role === "ai").length;
      trackTransfersPinClarificationClicked({
        artworkId,
        sessionId,
        pinId: currentPin.id,
        optionValue: "",
        turnIndex: userCount + 1,
        userMessagesCount: userCount,
        aiMessagesCount: aiCount,
        optionType: "custom"
      });
    }
  }

  function handleCustomInputChange(question, value) {
    setClarificationResponses(prev => ({
      ...prev,
      [question]: {
        selected: prev[question]?.selected ?? null,
        custom: value,
        isTypingCustom: true
      }
    }));
  }

  function trackPinSelected(pinId, previousPinId, pinState) {
    if (sessionId && artworkId) {
      trackTransfersPinSelected({
        artworkId,
        sessionId,
        pinId,
        previousPinId: previousPinId ?? null,
        pinState
      });
    }
  }

  function calculateUnansweredPositions() {
    const scrollContainer = clarificationScrollRef.current;
    if (!scrollContainer) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    let above = 0;
    let below = 0;

    pendingPillClarifications().forEach(clarification => {
      const isUnanswered = !getClarificationAnswer(clarificationResponses[clarification.question]);
      if (isUnanswered) {
        const labelValue = clarification.label || clarification.question;
        const element = scrollContainer.querySelector(
          `[data-clarification-label="${CSS.escape(labelValue)}"]`
        );
        if (element) {
          const elementRect = element.getBoundingClientRect();
          if (elementRect.bottom < containerRect.top) {
            above += 1;
          } else if (elementRect.top > containerRect.bottom) {
            below += 1;
          }
        }
      }
    });

    setUnansweredAbove(above);
    setUnansweredBelow(below);
  }

  function scrollToFirstUnanswered(direction) {
    const scrollContainer = clarificationScrollRef.current;
    if (!scrollContainer) return;

    const containerRect = scrollContainer.getBoundingClientRect();

    for (const clarification of pendingPillClarifications()) {
      const isUnanswered = !getClarificationAnswer(clarificationResponses[clarification.question]);

      if (isUnanswered) {
        const labelValue = clarification.label || clarification.question;
        const element = scrollContainer.querySelector(
          `[data-clarification-label="${CSS.escape(labelValue)}"]`
        );

        if (element) {
          const elementRect = element.getBoundingClientRect();
          const isTargetDirection =
            direction === "above"
              ? elementRect.bottom < containerRect.top
              : elementRect.top > containerRect.bottom;

          if (isTargetDirection) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
            break;
          }
        }
      }
    }
  }

  function formatClarificationResponses() {
    const parts = [];
    const clarifications = [];

    for (const clarification of pendingPillClarifications()) {
      const value = getClarificationAnswer(clarificationResponses[clarification.question]);
      const isCustom = clarificationResponses[clarification.question]?.isTypingCustom ?? false;
      if (value) {
        const labelText = clarification.label || clarification.question;
        parts.push(`"${labelText}": ${value}`);
        clarifications.push({
          question: clarification.question,
          value,
          option_type: isCustom ? "custom" : "suggested"
        });
      }
    }

    return { clarificationText: parts.join("\n"), clarifications };
  }

  function dispatchPinMessage({
    sessionId: sid,
    artworkId: aid,
    pinId,
    coordinates,
    message,
    isFirstMessage
  }) {
    abortController?.abort();
    abortController = new AbortController();

    const priorPins = pins
      .filter(p => p.id !== pinId)
      .map(p => ({
        pinId: p.id,
        coordinates: p.coordinates,
        state: p.state,
        instruction: p.instruction ?? ""
      }));

    submitPinMessage(
      {
        sessionId: sid,
        pinId,
        coordinates,
        userMessage: message,
        isFirstMessage,
        priorPins
      },
      {
        onAcknowledgment(payload) {
          updatePin(pinId, pin => ({
            ...pin,
            thinkingText: payload.message || undefined
          }));
        },
        onResult(payload) {
          const nextState =
            payload.status === "ready" ||
            payload.status === "needs_clarification" ||
            payload.status === "unsupported"
              ? payload.status
              : "ready";

          const payloadClarifications = payload.clarifications ?? [];
          const resultMessage = payload.message || "Done.";

          const askingClarifications = payloadClarifications.filter(
            c => c.status === "asking" && c.options.length > 0
          );
          const hasClarifications =
            nextState === "needs_clarification" && askingClarifications.length > 0;

          updatePin(pinId, pin => {
            const areasOfInterestUpdate = payload.aoiResult
              ? {
                  areasOfInterest: payload.aoiResult,
                  highlightAreasEnabled: pin.highlightAreasEnabled ?? true
                }
              : {};

            return {
              ...pin,
              state: nextState,
              instruction: payload.instruction,
              thinkingText: undefined,
              ...areasOfInterestUpdate,
              messages: [
                ...pin.messages,
                {
                  id: generateUUID(),
                  role: "ai",
                  content: resultMessage,
                  timestamp: Date.now(),
                  ...(hasClarifications && {
                    clarifications: payloadClarifications,
                    textInfo: payload.text
                  })
                }
              ]
            };
          });

          if (hasClarifications) {
            setClarificationResponses({});
          }

          savedClarificationResponses = null;
          savedPinState = null;
          savedMessages = null;
          savedComposerText = null;

          const currentPin = pins.find(p => p.id === pinId);
          const userCount = (currentPin?.messages ?? []).filter(m => m.role === "user").length;
          const aiCount = (currentPin?.messages ?? []).filter(m => m.role === "ai").length + 1;
          const trackingData = {
            artworkId: aid,
            sessionId: sid,
            pinId,
            userMessagesCount: userCount,
            aiMessagesCount: aiCount
          };

          if (!sid || !aid) return;

          if (nextState === "ready") {
            trackTransfersPinReady(trackingData);
          } else if (nextState === "unsupported") {
            trackTransfersPinUnsupported(trackingData);
          } else if (nextState === "needs_clarification") {
            trackTransfersPinNeedsClarification({
              ...trackingData,
              optionsCount: askingClarifications.reduce((sum, c) => sum + c.options.length, 0),
              questionType: askingClarifications.map(c => c.type).join(",")
            });
          }
        },
        onError() {
          const currentPin = pins.find(p => p.id === pinId);
          const userCount = (currentPin?.messages ?? []).filter(m => m.role === "user").length;
          const aiCount = (currentPin?.messages ?? []).filter(m => m.role === "ai").length;

          if (sessionId && artworkId) {
            trackTransfersPinError({
              artworkId,
              sessionId,
              pinId,
              errorType: "processing_error",
              userMessagesCount: userCount,
              aiMessagesCount: aiCount
            });
          }

          const previousState = savedPinState ?? "draft";
          const previousMessages = savedMessages;

          updatePin(pinId, pin => ({
            ...pin,
            state: previousState,
            thinkingText: undefined,
            messages: previousMessages ?? pin.messages.slice(0, -1)
          }));

          if (savedClarificationResponses) {
            setClarificationResponses(savedClarificationResponses);
            savedClarificationResponses = null;
          }

          if (savedComposerText !== null) {
            setComposerText(savedComposerText);
            savedComposerText = null;
          }

          savedPinState = null;
          savedMessages = null;

          toast?.error({ title: "Something went wrong", description: "Please try again." });
        }
      },
      {
        isPinActive: id => pins.some(p => p.id === id) && !removingPinIds.has(id),
        signal: abortController.signal
      }
    );
  }

  function handleSubmit() {
    const currentActivePin = activePin();
    if (!currentActivePin) return;
    if (currentActivePin.state === "processing") return;

    const { clarificationText, clarifications } = formatClarificationResponses();
    const messageText = composerText.trim();
    const fullMessage = [clarificationText, messageText].filter(Boolean).join("\n");

    if (!fullMessage) return;

    const pinId = currentActivePin.id;
    const userMessageId = generateUUID();
    const isFirstMessage = currentActivePin.messages.filter(m => m.role === "user").length === 0;
    const turnIndex = currentActivePin.messages.filter(m => m.role === "user").length + 1;
    const userMessagesCount = currentActivePin.messages.filter(m => m.role === "user").length;
    const aiMessagesCount = currentActivePin.messages.filter(m => m.role === "ai").length;

    if (sessionId && artworkId) {
      trackTransfersPinMessageSubmitted({
        artworkId,
        sessionId,
        pinId,
        isFirstMessage,
        turnIndex,
        userMessagesCount,
        aiMessagesCount,
        clarifications
      });
    }

    savedClarificationResponses = { ...clarificationResponses };
    savedPinState = currentActivePin.state;
    savedMessages = [...currentActivePin.messages];
    savedComposerText = composerText;

    updatePin(pinId, pin => ({
      ...pin,
      state: "processing",
      thinkingText: undefined,
      messages: [
        ...pin.messages.map(m => ({ ...m, clarifications: undefined })),
        { id: userMessageId, role: "user", content: fullMessage, timestamp: Date.now() }
      ]
    }));

    setComposerText("");
    setClarificationResponses({});
    setLastEditedPinId(pinId);

    if (!sessionId) return;

    dispatchPinMessage({
      sessionId,
      artworkId,
      pinId,
      coordinates: currentActivePin.coordinates,
      message: fullMessage,
      isFirstMessage
    });
  }

  useEffect(() => {
    if (mobileView === "list") {
      setIsPanelExpanded(false);
      setHoveredPinId(null);
      setHoveredPinSource(null);
    }
  }, [mobileView]);

  useEffect(() => {
    if (messagesScrollRef.current && activePin()?.messages.length) {
      setTimeout(() => {
        if (messagesScrollRef.current) {
          messagesScrollRef.current.scrollTop = messagesScrollRef.current.scrollHeight;
        }
      }, 0);
    }
  }, [activePin()?.messages.length]);

  useEffect(() => {
    const active = activePin();
    if (active?.state === "ready" || active?.state === "unsupported") {
      setIsPanelExpanded(false);
    }
  }, [activePin()?.state]);

  useEffect(() => {
    setComposerText("");
    setClarificationResponses({});
  }, [activePinId]);

  useEffect(() => {
    if (pendingCursorPosition !== null && textareaRef.current) {
      setTimeout(() => {
        const pos = pendingCursorPosition;
        if (textareaRef.current && pos !== null) {
          textareaRef.current.selectionStart = pos;
          textareaRef.current.selectionEnd = pos;
          textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
        }
        setPendingCursorPosition(null);
      }, 0);
    }
  }, [composerText, pendingCursorPosition]);

  useEffect(() => {
    if (pendingPillClarifications().length > 0) {
      calculateUnansweredPositions();
    }
  }, [pendingPillClarifications().length, clarificationResponses]);

  useEffect(() => {
    if (pins.length === 0) return;

    const incompletePin = pins.find(p => p.state !== "ready" && p.state !== "unsupported");
    if (!incompletePin) return;

    if (isDesktop) {
      if (!activePinId) {
        setActivePinId(incompletePin.id);
        setTimeout(() => {
          const pinElement = pinsContainerRef.current?.querySelector(
            `[data-pin-id="${incompletePin.id}"]`
          );
          pinElement?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      }
    } else if (mobileView === "list") {
      setActivePinId(incompletePin.id);
      setMobileView("detail");
    }
  }, [pins, mobileView, activePinId, isDesktop]);

  useEffect(() => {
    if (!isDesktop && pins.length === 0) {
      setMobileView("list");
    }
  }, [pins.length, isDesktop]);

  useEffect(() => {
    const active = activePin();
    if (!isDesktop && mobileView === "detail" && active) {
      const isCompleted = active.state === "ready" || active.state === "unsupported";
      if (isCompleted) {
        setHoveredPinId(active.id);
        setHoveredPinSource("panel");
      }
    }
  }, [mobileView, activePin(), isDesktop]);

  useEffect(() => {
    if (!isDesktop && activePinId) {
      const pin = pins.find(p => p.id === activePinId);
      if (pin?.isNew && pin?.state === "draft" && pin?.messages.length === 0) {
        setMobileView("detail");
      }
    }
  }, [activePinId, pins, isDesktop]);

  const autoSubmitTriggeredRef = useRef(false);

  useEffect(() => {
    if (!pendingInitialMessage) return;
    if (autoSubmitTriggeredRef.current) return;
    if (pins.length === 0 || !activePinId) return;

    const pin = pins.find(p => p.id === activePinId);
    if (!pin || pin.state !== "draft" || pin.messages.length > 0) return;

    if (!sessionId) return;

    autoSubmitTriggeredRef.current = true;
    const message = pendingInitialMessage;

    const pinId = pin.id;
    const userMessageId = generateUUID();

    savedPinState = pin.state;
    savedMessages = [...pin.messages];
    savedComposerText = message;

    updatePin(pinId, p => ({
      ...p,
      state: "processing",
      thinkingText: undefined,
      messages: [{ id: userMessageId, role: "user", content: message, timestamp: Date.now() }]
    }));
    setLastEditedPinId(pinId);

    if (artworkId && sessionId) {
      trackTransfersPinMessageSubmitted({
        artworkId,
        sessionId,
        pinId,
        isFirstMessage: true,
        turnIndex: 1,
        userMessagesCount: 1,
        aiMessagesCount: 0,
        clarifications: []
      });
    }

    dispatchPinMessage({
      sessionId,
      artworkId,
      pinId,
      coordinates: pin.coordinates,
      message,
      isFirstMessage: true
    });
  }, [pins, activePinId, sessionId, artworkId, pendingInitialMessage]);

  function hydratePins(pinsData) {
    setPins(
      pinsData.map(p => {
        const messages = Array.isArray(p.messages)
          ? p.messages
              .filter(m => m?.role !== "ai_ack")
              .map(m => ({
                id: m.id || "",
                role: m.role || "user",
                content: m.content || "",
                timestamp: m.timestamp || Date.now(),
                ...(m.options ? { options: m.options } : {}),
                ...(m.questionType ? { questionType: m.questionType } : {}),
                ...(m.clarifications ? { clarifications: m.clarifications } : {}),
                ...(m.textInfo ? { textInfo: m.textInfo } : {})
              }))
          : [];

        if (messages.length === 0 && p.message) {
          messages.push({
            id: `msg-${p.pinId}`,
            role: "ai",
            content: p.message,
            timestamp: Date.now()
          });
        }

        const state = p.status || p.state || "draft";

        return {
          id: p.pinId || p.id || "",
          coordinates: p.coordinates || { x: 0, y: 0 },
          state,
          instruction: p.instruction || p.summary,
          messages,
          areasOfInterest: p.areaOfInterest,
          segmentationMaskUrl: p.segmentationMaskUrl,
          isNew: false
        };
      })
    );

    setActivePinId(null);
  }

  useEffect(
    () => () => {
      abortController?.abort();
      setPins([]);
      setActivePinId(null);
      setLastEditedPinId(null);
    },
    []
  );

  return {
    PIN_COLORS,
    MAX_PINS,

    isDesktop,
    pins,
    setPins,
    activePinId,
    setActivePinId,
    hoveredPinId,
    setHoveredPinId,
    hoveredPinSource,
    setHoveredPinSource,
    composerText,
    removingPinIds,
    pendingDeletePinId,
    clarificationResponses,
    mobileView,
    isPanelExpanded,
    editingPinId,
    isUpdatingPinInstruction,
    lastEditedPinId,
    unansweredAbove,
    unansweredBelow,
    showPins,
    setShowPins,

    textareaRef,
    pendingCursorPosition,
    clarificationScrollRef,
    messagesScrollRef,
    pinsContainerRef,

    activePin,
    editingPin,
    hasCompletedPin,
    readyPinsCount,
    hasExistingPin,
    hasAiAssistanceOngoing,
    hasUnfinishedPin,
    canAddNewPin,
    canRepositionActivePin,
    lastAiMessageWithClarifications,
    pendingPillClarifications,
    fontClarification,
    colorClarification,
    selectedFontValue,
    selectedColorValue,
    hasAnyClarificationResponse,

    addPin,
    repositionActivePin,
    handleDeletePin,
    openDeletePinModal,
    closeDeletePinModal,
    confirmDeletePin,
    openEditPinModal,
    closeEditPinModal,
    handleUpdatePinInstruction,
    handleSelectClarificationOption,
    handleToggleCustomInput,
    handleCustomInputChange,
    handleSubmit,
    isComposerTextValid,
    trackPinSelected,
    calculateUnansweredPositions,
    scrollToFirstUnanswered,
    hydratePins
  };
}
